<template>
    <modal :open="open" @close="$emit('close')" size="3xl">
        <div v-if="offer">
            <div class="flex items-start justify-between">
                <div>
                    <h3 class="text-lg font-medium text-white">{{ offer.name }}</h3>
                    <p class="text-sm text-slate-400">Código: {{ offer.code }}</p>
                </div>
                <span
                    class="text-sm font-medium px-2.5 py-1.5 rounded"
                    :class="offer.is_active ? 'bg-green-500 text-white' : 'bg-red-500 text-white'"
                >
                    {{ offer.is_active ? 'Ativa' : 'Inativa' }}
                </span>
            </div>

            <div class="mt-4 flex items-center gap-4">
                <p class="text-2xl font-semibold text-white">{{ formattedPrice }}</p>
                <a
                    v-if="checkoutUrl"
                    :href="checkoutUrl"
                    target="_blank"
                    rel="noopener"
                    class="btn-primary-sm text-sm inline-flex items-center px-4 py-2 rounded-md bg-indigo-500 hover:bg-indigo-400 text-white"
                >
                    Ir para Checkout
                </a>
            </div>

            <div v-if="offer.is_subscription && offer.subscription" class="mt-6">
                <h4 class="text-sm font-bold text-slate-400 uppercase mb-2">Recorrência e Trial</h4>
                <config-grid :fields="subscriptionFields"></config-grid>
            </div>

            <div v-if="offer.freight" class="mt-6">
                <h4 class="text-sm font-bold text-slate-400 uppercase mb-2">Frete</h4>
                <config-grid :fields="freightFields"></config-grid>
            </div>

            <div class="mt-6">
                <h4 class="text-sm font-bold text-slate-400 uppercase mb-2">Configurações técnicas</h4>
                <config-grid :fields="offerFields"></config-grid>
            </div>
        </div>
    </modal>
</template>

<script>
import Modal from "../layouts/Modal.vue";

const EXCLUDED_KEYS = [
    'id', 'product_id', 'created_at', 'updated_at', 'deleted_at',
    'subscription', 'freight', 'name', 'code', 'is_active', 'price', 'currency',
];

const PERCENTAGE_KEYS = [
    'affiliates_percentage', 'affiliates_subscription_percentage',
    'credit_card_non_installment_discount_percentage', 'bank_slip_discount_percentage',
    'pix_discount_percentage', 'credit_card_installment_discount_percentage',
    'pix_recurring_discount_percentage',
];

const CURRENCY_KEYS = ['first_charge_price', 'fake_initial_price'];

const LABELS = {
    description: 'Descrição',
    product_checkout_description: 'Descrição no checkout',
    is_free: 'Gratuita',
    is_hidden: 'Oculta',
    is_email_required: 'Email obrigatório',
    type_of_charge: 'Tipo de cobrança',
    quantity_items: 'Quantidade de itens',
    days_of_access: 'Dias de acesso',
    allow_credit_card: 'Permite cartão de crédito',
    allow_two_credit_cards: 'Permite dois cartões',
    allow_bank_slip: 'Permite boleto',
    allow_pix: 'Permite Pix',
    allow_bank_slip_installments: 'Permite boleto parcelado',
    bank_slip_installments_limit: 'Limite de parcelas no boleto',
    bank_slip_days_due: 'Dias para vencimento do boleto',
    allow_credit_card_installments: 'Permite cartão parcelado',
    credit_card_installments_options: 'Opções de parcelamento',
    producer_pays_interest: 'Produtor paga os juros',
    allow_smart_installment: 'Parcelamento inteligente',
    allow_infinite_charge: 'Cobrança recorrente infinita',
    days_to_expire_pix: 'Dias para expirar o Pix',
    allow_progressive_discount: 'Desconto progressivo',
    progressive_discounts: 'Regras de desconto progressivo',
    allow_payment_method_discounts: 'Desconto por método de pagamento',
    allow_affiliates: 'Permite afiliados',
    links_visible_to_affiliates: 'Links visíveis para afiliados',
    available_affiliates: 'Afiliados disponíveis',
    auto_affiliation: 'Auto afiliação',
    commission_only_first_installment: 'Comissão só na primeira parcela',
    transfer_fixed_fee_to_buyer: 'Transferir taxa fixa ao comprador',
    request_address_after_purchase: 'Solicita endereço após compra',
    allow_email_confirmation: 'Confirmação de email',
    interval: 'Intervalo de cobrança (dias)',
    trial_days: 'Dias de trial',
    first_charge_price: 'Valor da primeira cobrança',
    is_extension: 'É extensão',
    extension_length: 'Duração da extensão',
};

function humanize(key) {
    return key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, letter => letter.toUpperCase());
}

export default {
    name: 'OfferDetailModal',
    components: {
        Modal,
        ConfigGrid: {
            props: ['fields'],
            template: `
                <dl class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div v-for="field in fields" :key="field.key">
                        <dt class="text-xs text-slate-400">{{ field.label }}</dt>
                        <dd class="text-sm text-white wrap-break-word">{{ field.value }}</dd>
                    </div>
                </dl>
            `,
        },
    },
    props: {
        open: { type: Boolean, default: false },
        offer: { type: Object, default: null },
    },
    emits: ['close'],
    computed: {
        formattedPrice() {
            if (!this.offer) {
                return '';
            }

            return this.offer.currency
                ? this.$root.formatOtherCurrency(this.offer.price, this.offer.currency)
                : this.$root.formatCurrency(this.offer.price);
        },
        checkoutUrl() {
            const baseUrl = import.meta.env.VITE_CHECKOUT_FRONT_URL;

            if (!baseUrl || !this.offer?.code) {
                return null;
            }

            return `${baseUrl.replace(/\/$/, '')}/${this.offer.code}`;
        },
        subscriptionFields() {
            return this.buildFields(this.offer?.subscription, ['id', 'offer_id', 'created_at', 'updated_at', 'deleted_at']);
        },
        freightFields() {
            return this.buildFields(this.offer?.freight, ['id', 'offer_id', 'created_at', 'updated_at', 'deleted_at']);
        },
        offerFields() {
            return this.buildFields(this.offer, EXCLUDED_KEYS);
        },
    },
    methods: {
        buildFields(source, excludedKeys) {
            if (!source) {
                return [];
            }

            return Object.keys(source)
                .filter(key => !excludedKeys.includes(key))
                .map(key => ({
                    key,
                    label: LABELS[key] || humanize(key),
                    value: this.formatValue(key, source[key]),
                }));
        },
        formatValue(key, value) {
            if (value === null || value === undefined || value === '') {
                return '—';
            }

            if (typeof value === 'boolean') {
                return value ? 'Sim' : 'Não';
            }

            if (PERCENTAGE_KEYS.includes(key)) {
                return `${value}%`;
            }

            if (CURRENCY_KEYS.includes(key)) {
                return this.$root.formatCurrency(value);
            }

            if (Array.isArray(value)) {
                return value.length ? value.join(', ') : '—';
            }

            if (typeof value === 'object') {
                return JSON.stringify(value);
            }

            return value;
        },
    },
};
</script>
